import React, { useEffect, useRef, useState } from "react";

export default function LeadCaptureModal({
  open,
  onOpenChange,
  serviceOptions = [
    { value: "", label: "Select a service..." },
    { value: "cybersecurity", label: "Cybersecurity Services" },
    { value: "ai", label: "Artificial Intelligence Services" },
    { value: "web", label: "Web Development Services" },
    { value: "digital", label: "Digital Marketing" },
  ],
  title = "Get a Free Consultation",
  onSubmit: onSubmitProp, // async (data) => {}
}) {
  const firstInputRef = useRef(null);
  const dialogRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    email: "",
    phone: "",       // ✅ NEW
    service: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Lock scroll + esc to close + focus first input
  useEffect(() => {
    if (!open) return;

    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstInputRef.current?.focus(), 50);

    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange?.(false);
      // rudimentary focus trap
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const validate = () => {
    const next = {};
    const name = formData.fullName.trim();
    //const company = formData.companyName.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const service = formData.service;

    if (!name) next.fullName = "Full name is required";
    if (!service) next.service = "Please select a service";

    const hasEmail = email.length > 0;
    const hasPhone = phone.length > 0;

    // At least one contact method required
    if (!hasEmail && !hasPhone) {
      const msg = "Provide an email or a phone number.";
      next.email = msg;
      next.phone = msg;
    } else {
      // validate email if provided
      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        next.email = "Enter a valid email";
      }
      // validate phone if provided (>=7 digits total)
      if (hasPhone) {
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 7) {
          next.phone = "Enter a valid phone number";
        }
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((er) => ({ ...er, [e.target.name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      if (onSubmitProp) {
        await onSubmitProp(formData); // payload now includes phone
      } else {
        console.log("Lead form payload:", formData);
      }
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .leadModal__overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.55);
          display: ${open ? "flex" : "none"};
          align-items: center; justify-content: center;
          z-index: 9999; padding: 20px;
        }
        .leadModal__content {
          width: 100%; max-width: 560px;
          background: #fff; border-radius: 16px;
          box-shadow: 0 20px 80px rgba(0,0,0,0.25);
          overflow: hidden;
          transform: translateY(${open ? 0 : 15}px);
          opacity: ${open ? 1 : 0};
          transition: all .35s ease;
        }
        .leadModal__header {
          padding: 20px 24px;
          background: linear-gradient(135deg, #00ffff 0%, #007acc 100%);
          color: #fff; display: flex; align-items: center; justify-content: space-between;
        }
        .leadModal__title { font-weight: 700; font-size: 18px; }
        .leadModal__close {
          border: none; background: transparent; color: #fff;
          font-size: 22px; cursor: pointer; line-height: 1;
        }
        .leadModal__body { padding: 20px 24px 24px; }
        .leadModal__row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 520px) { .leadModal__row { grid-template-columns: 1fr; } }
        .leadModal__group { margin-bottom: 14px; }
        .leadModal__label { display:block; font-weight:600; color:#333; margin-bottom:6px; font-size:14px; }
        .leadModal__input, .leadModal__select {
          width:100%; padding:12px 14px; border-radius:10px; border:1px solid #dde1e5;
          outline:none; font-size:14px; transition:border .2s ease, box-shadow .2s ease;
          background:#fff;
        }
        .leadModal__input:focus, .leadModal__select:focus {
          border-color:#007acc; box-shadow:0 0 0 3px rgba(0,122,204,.15);
        }
        .leadModal__error { color:#dc3545; font-size:12px; margin-top:6px; }
        .leadModal__actions { display:flex; gap:10px; justify-content:flex-end; margin-top:14px; }
        .leadModal__btn {
          border:none; border-radius:12px; padding:12px 16px; font-weight:700; cursor:pointer;
          transition:transform .15s ease, box-shadow .2s ease;
        }
        .leadModal__btn:active { transform: translateY(1px); }
        .leadModal__btn--primary {
          background: linear-gradient(135deg, #00ffff 0%, #007acc 100%); color:#fff;
          box-shadow:0 6px 22px rgba(0,122,204,.22);
        }
        .leadModal__btn--ghost {
          background:#f4f6f8; color:#333;
        }
        .leadModal__success {
          text-align:center; padding:36px 24px;
        }
        .leadModal__success h3 { margin:0 0 8px; }
        .leadModal__success p { color:#555; margin:0 0 18px; }
      `}</style>

      <div
        className="leadModal__overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leadModalTitle"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onOpenChange?.(false);
        }}
      >
        <div className="leadModal__content" ref={dialogRef}>
          <div className="leadModal__header">
            <div id="leadModalTitle" className="leadModal__title">
              {submitted ? "Thank you!" : title}
            </div>
            <button
              type="button"
              className="leadModal__close"
              aria-label="Close"
              onClick={() => onOpenChange?.(false)}
            >
              ×
            </button>
          </div>

          <div className="leadModal__body">
            {submitted ? (
              <div className="leadModal__success">
                <h3>We’ve received your details.</h3>
                <p>Our team will reach out shortly.</p>
                <div className="leadModal__actions" style={{ justifyContent: "center" }}>
                  <button
                    className="leadModal__btn leadModal__btn--primary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="leadModal__row">
                  <div className="leadModal__group">
                    <label className="leadModal__label" htmlFor="fullName">Full Name</label>
                    <input
                      id="fullName"
                      name="fullName"
                      className="leadModal__input"
                      value={formData.fullName}
                      onChange={handleChange}
                      ref={firstInputRef}
                      autoComplete="name"
                      aria-invalid={Boolean(errors.fullName)}
                    />
                    {errors.fullName && <div className="leadModal__error">{errors.fullName}</div>}
                  </div>

                  <div className="leadModal__group">
                    <label className="leadModal__label" htmlFor="companyName">Company Name</label>
                    <input
                      id="companyName"
                      name="companyName"
                      className="leadModal__input"
                      value={formData.companyName}
                      onChange={handleChange}
                      autoComplete="organization"
                      aria-invalid={Boolean(errors.companyName)}
                    />
                    {errors.companyName && <div className="leadModal__error">{errors.companyName}</div>}
                  </div>
                </div>

                {/* Email + Phone row */}
                <div className="leadModal__row">
                  <div className="leadModal__group">
                    <label className="leadModal__label" htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="leadModal__input"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                    />
                    {errors.email && <div className="leadModal__error">{errors.email}</div>}
                  </div>

                  <div className="leadModal__group">
                    <label className="leadModal__label" htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      className="leadModal__input"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="+1 (555) 555-1234"
                      aria-invalid={Boolean(errors.phone)}
                    />
                    {errors.phone && <div className="leadModal__error">{errors.phone}</div>}
                  </div>
                </div>

                <div className="leadModal__group">
                  <label className="leadModal__label" htmlFor="service">Services Required</label>
                  <select
                    id="service"
                    name="service"
                    className="leadModal__select"
                    value={formData.service}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.service)}
                  >
                    {serviceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {errors.service && <div className="leadModal__error">{errors.service}</div>}
                </div>

                <div className="leadModal__actions">
                  <button
                    type="button"
                    className="leadModal__btn leadModal__btn--ghost"
                    onClick={() => onOpenChange?.(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="leadModal__btn leadModal__btn--primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
